import React, { useMemo } from 'react';
import { parse, format } from 'date-fns';
import { getParkKey, getParkDisplayName, PARK_KEYS } from '../../../../utils/parkKeys';
import { getMustCountsByPark, getMustDoRecommendedCap, getParksOverMustDoRecommended } from '../../../../utils/bucketCaps';

const TripSummaryPanel = ({ formData }) => {
  const summary = useMemo(() => {
    const basic = formData.basicInformation || {};
    const group = formData.groupInfo || {};
    const parks = formData.parkVisitPlans || {};
    const accommodation = formData.accommodation || {};
    const dates = {
      start: basic.startDate ? format(parse(basic.startDate, 'yyyy-MM-dd', new Date()), 'MMM d, yyyy') : null,
      end: basic.endDate ? format(parse(basic.endDate, 'yyyy-MM-dd', new Date()), 'MMM d, yyyy') : null,
    };

    const totalAdults = parseInt(group.adults || '0', 10) || 0;
    const totalKids = parseInt(group.kids || '0', 10) || 0;
    const totalGuests = totalAdults + totalKids;

    const selectedParks = parks.parks || [];
    const parksList = Array.isArray(selectedParks) ? selectedParks.map((p) => String(p)) : [];

    const ar = formData.attractionRideStrategy || {};
    const mustRidesByPark = ar.mustRidesByPark || {};
    const mustCountsByPark = getMustCountsByPark(mustRidesByPark);

    const selectedKeys = new Set(
      parksList.flatMap((p) => {
        const key = getParkKey(p);
        if (key) return [key];
        if (PARK_KEYS.includes(p)) return [p];
        return [];
      })
    );
    const hasMustRideCounts = Object.keys(mustRidesByPark).some(
      (k) => (Array.isArray(mustRidesByPark[k]) ? mustRidesByPark[k].length : 0) > 0
    );
    const keysToShow =
      selectedKeys.size > 0
        ? PARK_KEYS.filter((key) => selectedKeys.has(key))
        : hasMustRideCounts
          ? PARK_KEYS.filter((key) => (mustCountsByPark[key] ?? 0) > 0)
          : [];
    const themeParksList = keysToShow.map((key) => ({
      key,
      displayName: getParkDisplayName(key),
    }));

    const tripStartDate =
      basic.startDate && !isNaN(new Date(basic.startDate))
        ? new Date(basic.startDate)
        : null;

    const totalParkDays = ['mkDays', 'hsDays', 'epcotDays', 'akDays', 'tlDays', 'bbDays']
      .reduce((sum, field) => sum + (parseInt(parks[field] || '0', 10) || 0), 0);

    const resort = accommodation.resort || accommodation.otherHotel || null;
    const stayingOnProperty = accommodation.stayingOnProperty;
    const hasParkHopper = formData.parkTicketAccess?.hasParkHopper === 'Yes';
    const ticketType = formData.parkTicketAccess?.ticketType || '';

    const travelDatesText =
      dates.start && dates.end ? `${dates.start} – ${dates.end}` : '';
    const groupSizeText =
      totalGuests > 0
        ? totalAdults > 0 || totalKids > 0
          ? `${totalGuests} ${totalGuests === 1 ? 'guest' : 'guests'} (${totalAdults} adult${totalAdults !== 1 ? 's' : ''}${totalKids > 0 ? `, ${totalKids} child${totalKids !== 1 ? 'ren' : ''}` : ''})`
          : `${totalGuests} ${totalGuests === 1 ? 'guest' : 'guests'}`
        : '';
    const parkDaysText =
      totalParkDays > 0 ? `${totalParkDays} day${totalParkDays !== 1 ? 's' : ''}` : '';
    const ticketTypeText =
      ticketType + (hasParkHopper ? ' · Park Hopper' : '');

    const rideStrategy = ar.rideStrategy;
    const rideSelectionMode = typeof rideStrategy === 'string' ? rideStrategy : '';
    const flatMustRides = ar.mustRides || [];
    const selectedRides = flatMustRides.length > 0
      ? flatMustRides
      : Object.keys(mustRidesByPark).reduce((acc, key) => {
          const arr = Array.isArray(mustRidesByPark[key]) ? mustRidesByPark[key] : [];
          return acc.concat(arr);
        }, []);

    return {
      dates,
      totalGuests,
      totalAdults,
      totalKids,
      selectedParks,
      parksList,
      themeParksList,
      totalParkDays,
      resort,
      stayingOnProperty,
      hasParkHopper,
      ticketType,
      travelDatesText,
      groupSizeText,
      parkDaysText,
      ticketTypeText,
      rideSelectionMode,
      selectedRides,
      mustCountsByPark,
      tripStartDate,
      parksOverMustDoRecommended: getParksOverMustDoRecommended(mustCountsByPark),
    };
  }, [formData]);

  return (
    <div className="questionnaire-v2-summary-panel">
      <div className="summary-header">
        <h3 className="m-0 mb-2 text-xl font-semibold">Your Trip Overview</h3>
        <p className="summary-subtitle m-0 mb-4 text-sm">As you answer, your trip overview updates here.</p>
      </div>

      <div className="summary-content">
        {(summary.travelDatesText || summary.groupSizeText || summary.parksList.length > 0 || summary.parkDaysText || summary.ticketTypeText || summary.resort) ? (
          <div className="trip-overview">
            {(summary.travelDatesText || summary.groupSizeText) ? (
              <>
                <div className="trip-overview__stack">
                  {summary.travelDatesText ? (
                    <div className="trip-overview__item">
                      <div className="trip-overview__label">Travel Dates</div>
                      <div className="trip-overview__value">{summary.travelDatesText}</div>
                    </div>
                  ) : null}
                  {summary.groupSizeText ? (
                    <div className="trip-overview__item">
                      <div className="trip-overview__label">Group Size</div>
                      <div className="trip-overview__value">{summary.groupSizeText}</div>
                    </div>
                  ) : null}
                </div>
                <div className="trip-overview__divider" />
              </>
            ) : null}

            {summary.ticketTypeText ? (
              <>
                <div className="trip-overview__row">
                  <div className="trip-overview__label">Ticket Type</div>
                  <div className="trip-overview__value">{summary.ticketTypeText}</div>
                </div>
                <div className="trip-overview__divider" />
              </>
            ) : null}

            {summary.parksList.length > 0 ? (
              <>
                <div className="trip-overview__item">
                  <div className="trip-overview__label">Parks</div>
                  <div className="trip-chip-list">
                    {summary.parksList.map((p) => (
                      <span key={p} className="trip-chip">{p}</span>
                    ))}
                  </div>
                </div>
                <div className="trip-overview__divider" />
              </>
            ) : null}

            {summary.parkDaysText ? (
              <>
                <div className="trip-overview__row">
                  <div className="trip-overview__label">Park Days</div>
                  <div className="trip-overview__value">{summary.parkDaysText}</div>
                </div>
                <div className="trip-overview__divider" />
              </>
            ) : null}

            {summary.resort ? (
              <>
                <div className="trip-overview__row">
                  <div className="trip-overview__label">Accommodation</div>
                  <div className="trip-overview__value">{summary.resort}</div>
                </div>
                <div className="trip-overview__divider" />
              </>
            ) : null}

            {summary.themeParksList.length > 0 ? (
              <>
                <div className="trip-overview__item trip-overview__must-do">
                  <div className="trip-overview__label">Must-Do Rides</div>
                  <div className="summary-must-do-progress">
                    {summary.themeParksList.map(({ key, displayName }) => {
                      const count = summary.mustCountsByPark[key] ?? 0;
                      const recommended = getMustDoRecommendedCap(key);
                      const overRecommended = recommended > 0 && count > recommended;
                      const pct = recommended > 0 ? Math.min(100, (count / recommended) * 100) : 0;
                      return (
                        <div
                          key={key}
                          className={`summary-must-do-progress__row ${overRecommended ? 'summary-must-do-progress__row--over-recommended' : ''}`}
                        >
                          <div className="summary-must-do-progress__header">
                            <span className="summary-must-do-progress__park">{displayName}</span>
                            <span className="summary-must-do-progress__count">{count} / {recommended}</span>
                          </div>
                          <div className="summary-must-do-progress__bar-wrap">
                            <div
                              className={`summary-must-do-progress__bar-fill ${overRecommended ? 'is-over-recommended' : ''}`}
                              style={{ width: `${pct}%` }}
                              role="progressbar"
                              aria-valuenow={count}
                              aria-valuemin={0}
                              aria-valuemax={recommended}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {summary.parksOverMustDoRecommended?.length > 0 ? (
                    <p className="summary-must-do-soft-cap-message" role="status">
                      You&apos;ve selected more Must-Do rides than recommended for one or more parks. Your itinerary may feel more rushed, but you can still continue.
                    </p>
                  ) : null}
                </div>
              </>
            ) : null}
          </div>
        ) : null}

        {!summary.dates.start && !summary.totalGuests && summary.selectedParks.length === 0 && (
          <div className="summary-skeleton" aria-hidden="true">
            <div className="summary-skeleton__row">
              <span className="summary-skeleton__label">Travel dates</span>
              <span className="summary-skeleton__bar summary-skeleton__bar--long" />
            </div>
            <div className="summary-skeleton__row">
              <span className="summary-skeleton__label">Group size</span>
              <span className="summary-skeleton__bar summary-skeleton__bar--medium" />
            </div>
            <div className="summary-skeleton__row">
              <span className="summary-skeleton__label">Parks</span>
              <span className="summary-skeleton__bar summary-skeleton__bar--medium" />
            </div>
            <div className="summary-skeleton__row">
              <span className="summary-skeleton__label">Accommodation</span>
              <span className="summary-skeleton__bar summary-skeleton__bar--long" />
            </div>
            <div className="summary-skeleton__row">
              <span className="summary-skeleton__label">Priorities</span>
              <span className="summary-skeleton__bar summary-skeleton__bar--short" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TripSummaryPanel;
